import { Helmet } from "react-helmet-async";

interface SeoProps {
  title: string;
  description: string;
  path: string;
  ogImage?: string;
  noindex?: boolean;
}

const SITE_NAME = "FortifyID";
const DEFAULT_OG = "/opengraph.jpg";

function resolveBaseUrl(): string {
  const explicit = import.meta.env.VITE_PUBLIC_BASE_URL as string | undefined;
  if (explicit) return explicit.replace(/\/$/, "");
  const domains = import.meta.env.VITE_REPLIT_DOMAINS as string | undefined;
  const first = domains?.split(",")[0]?.trim();
  if (first) return `https://${first}`;
  return "";
}

const BASE_URL = resolveBaseUrl();

export function Seo({ title, description, path, ogImage, noindex = false }: SeoProps) {
  const fullTitle = path === "/" ? `${SITE_NAME} — Identity-verified change requests for any business` : `${title} · ${SITE_NAME}`;
  const url = `${BASE_URL}${path}`;
  const og = ogImage ?? perPageOg(path) ?? DEFAULT_OG;
  return (
    <Helmet>
      <html lang="en-AU" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : <meta name="robots" content="index, follow" />}
      <link rel="canonical" href={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={og} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={og} />
    </Helmet>
  );
}

function perPageOg(path: string): string | null {
  const map: Record<string, string> = {
    "/": "/og/home.jpg",
    "/how-it-works": "/og/how-it-works.jpg",
    "/pricing": "/og/pricing.jpg",
    "/security": "/og/security.jpg",
    "/about": "/og/about.jpg",
    "/contact": "/og/contact.jpg",
  };
  return map[path] ?? null;
}
